import { useState, useMemo } from 'react';
import { BarChart, Bar, AreaChart, Area, ComposedChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { cn } from '@/lib/utils';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { C, MN } from '../shared/constants';
import { deliveredEnergy } from '../utils/financialCalculations';
import { FinancialDashboard } from '../financial';
import HubV2BusinessCase from '../HubV2BusinessCase';
import type { HubV2State } from '../hooks/useHubV2State';

function KPI({ icon, label, value, sub, color, status }: any) {
  const statusColors: Record<string, string> = { good: '#10B981', warn: '#F59E0B', bad: '#EF4444' };
  return (
    <div className="rounded-xl p-3 bg-white shadow-sm" style={{ border: `1px solid ${status ? statusColors[status] + '40' : '#D9E2EC'}` }}>
      <div className="text-[10px] text-[#8899AA] mb-0.5">{icon} {label}</div>
      <div className="text-lg font-bold" style={{ color: status ? statusColors[status] : color || '#0B1F4A' }}>{value}</div>
      {sub && <div className="text-[9px] text-[#8899AA] mt-0.5">{sub}</div>}
    </div>
  );
}

export default function StepResultaten({ state }: { state: HubV2State }) {
  const { r, financialModel, financialSim, financialSys, financialParams, handleFinancialParamChange, deg, pv, bE, bP, buP, gMax, numTrucks, resolvedDem, resolvedBaseDem, resolvedEvDem, blokstroomEnabled, effectiveBE, eff, dod } = state;
  const [tab, setTab] = useState('jaar');
  const [dpMo, setDpMo] = useState(5);

  const ssStatus = r.ss >= 85 ? 'good' : r.ss >= 70 ? 'warn' : 'bad';

  // Derde plek waar de resultaten van dezelfde simulatie te zien zijn (naast
  // HubSimulator) en de enige die "Totale Vraag" toonde zonder
  // erbij te zeggen hoeveel daarvan geleverd wordt. `deliveredEnergy(financialSim)`
  // is exact de omzetbasis van de Business Case-tab hiernaast — één formule, één
  // plek. Op de Wognum-invoer (pv 2 MWp, 20 MWh/10 MW BESS, 0,33 MW aansluiting,
  // geen aggregaat, 19.000 MWh, jaar 15): 4.149,9 geleverd van 18.994,3 gevraagd,
  // 14.844,4 MWh (78,2 %) ongedekt.
  const deliveredMwh = deliveredEnergy(financialSim);
  const unmetPct = r.dem > 0 ? (r.unmet / r.dem) * 100 : 0;
  // Zelfde 1 %-drempel als in V1 (HubSimulator): daaronder afrondingsruis.
  const unmetIsMaterial = unmetPct >= 1;

  // Dagprofiel data
  const dpData = useMemo(() => {
    if (!r.daily || !r.daily[dpMo]) return [];
    return r.daily[dpMo].map((d: any, i: number) => {
      const n = d.n || 1;
      return {
        h: `${i}:00`,
        pv: +(d.pv / n).toFixed(3),
        dem: +(d.dem / n).toFixed(3),
        grid: +((d.grid) / n).toFixed(3),
        batDischarge: +(d.bDischarge / n).toFixed(3),
        batChargePV: +(-(d.bChargePV) / n).toFixed(3),
        batChargeBlok: +(-(d.bChargeBlok) / n).toFixed(3),
        soc: d.n > 0 ? +((d.soc / d.n) / (effectiveBE * (dod / 100)) * 100).toFixed(1) : 0,
      };
    });
  }, [r.daily, dpMo, effectiveBE, dod]);

  // Supply/demand balance
  const supply = r.pv + r.gi + r.bu;
  const demand = r.dem + r.bLoss + r.ge + r.curt;

  return (
    <div className="space-y-4">
      {/* ONGEDEKTE VRAAG — zelfde waarschuwing als in HubSimulator en
          V1, boven de KPI-strook. */}
      {unmetIsMaterial && (
        <div className="rounded-xl border-2 border-[#FECACA] bg-[#FEF2F2] px-4 py-3">
          <div className="text-[13px] font-bold text-[#991B1B]">
            ⚠️ {unmetPct.toFixed(1)}% van de vraag blijft ongedekt — {r.unmet.toFixed(0)} van {r.dem.toFixed(0)} MWh
          </div>
          <div className="mt-1 text-[11px] leading-relaxed text-[#7F1D1D]">
            Net ({gMax} MW), batterij ({bP} MW / {effectiveBE.toFixed(1)} MWh) en noodaggregaat ({buP} MW) halen
            de vraag niet. De Business Case rekent daarom alleen met de {deliveredMwh.toFixed(0)} MWh die
            wél geleverd wordt: ongedekte vraag levert geen laadomzet en geen ERE op.
            Verhoog de aansluiting, het batterij- of noodvermogen, of verlaag de vraag.
          </div>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-2.5">
        <KPI icon="☀️" label="PV Productie" value={`${r.pv.toFixed(0)} MWh`} color={C.ch[0]} />
        <KPI icon="🔌" label="Totale Vraag" value={`${r.dem.toFixed(0)} MWh`} color={C.pri}
          sub={resolvedEvDem > 0 ? `Basis: ${resolvedBaseDem.toFixed(0)} + EV: ${resolvedEvDem.toFixed(0)}` : undefined} />
        {/* Geleverd naast Totale Vraag: alleen dit getal is omzet. */}
        <KPI icon="📦" label="Geleverd" value={`${deliveredMwh.toFixed(0)} MWh`}
          status={unmetIsMaterial ? 'bad' : 'good'}
          sub={r.unmet > 0.0001
            ? `${r.unmet.toFixed(0)} MWh ongedekt (${unmetPct.toFixed(1)}%) — niet factureerbaar`
            : 'volledige vraag gedekt'} />
        <KPI icon="✅" label="Zelfvoorziening" value={`${r.ss.toFixed(1)}%`} status={ssStatus}
          sub={r.ss >= 85 ? 'Uitstekend' : r.ss >= 70 ? 'Redelijk' : 'Aandacht nodig'} />
        <KPI icon="⚡" label="Grid Import" value={`${r.gi.toFixed(0)} MWh`} color={C.ch[4]} />
        <KPI icon="🏭" label="Backup" value={`${r.bu.toFixed(0)} MWh`} sub={`${r.bh.toFixed(0)} vollasturen`} color={C.org} />
        <KPI icon="✂️" label="Curtailed" value={`${r.curt.toFixed(0)} MWh`} color={C.acc} />
      </div>

      {/* Chart tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full justify-start rounded-none bg-transparent h-auto p-0 gap-1 mb-3 flex-wrap">
          {[
            ['jaar', '📊 Jaaroverzicht'],
            ['dag', '🕐 Dagprofiel'],
            ['balans', '⚖️ Energiebalans'],
            ['bc', '💰 Business Case'],
          ].map(([id, label]) => (
            <TabsTrigger key={id} value={id} className="text-[11px] font-semibold data-[state=active]:bg-[#0B1F4A] data-[state=active]:text-white data-[state=active]:shadow-sm rounded-lg px-4 py-2 border border-[#D9E2EC] data-[state=active]:border-[#0B1F4A]">
              {label}
            </TabsTrigger>
          ))}
        </TabsList>

        <div className="bg-white border border-[#D9E2EC] rounded-xl p-5 shadow-sm">
          {/* JAAROVERZICHT */}
          <TabsContent value="jaar" className="mt-0">
            <div className="text-sm font-bold text-[#0B1F4A] mb-1">📊 Maandelijkse Energiebalans</div>
            <div className="text-[11px] text-[#8899AA] mb-4">Opwek, vraag, grid en BESS activiteit per maand</div>
            <ResponsiveContainer width="100%" height={350}>
              <ComposedChart data={r.monthly} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.bdr} />
                <XAxis dataKey="m" tick={{ fontSize: 10, fill: C.t3 }} />
                <YAxis tick={{ fontSize: 10, fill: C.t3 }} label={{ value: 'MWh', angle: -90, position: 'insideLeft', style: { fontSize: 10, fill: C.t3 } }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Bar dataKey="pv" name="PV" fill={C.ch[0]} radius={[3, 3, 0, 0]} barSize={20} />
                <Bar dataKey="dem" name="Vraag" fill={C.ch[5]} radius={[3, 3, 0, 0]} barSize={20} />
                <Bar dataKey="gi" name="Grid Import" fill={C.ch[4]} radius={[3, 3, 0, 0]} barSize={20} />
                <Line dataKey="bu" name="Backup" stroke={C.org} strokeWidth={2} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </TabsContent>

          {/* DAGPROFIEL */}
          <TabsContent value="dag" className="mt-0">
            <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
              <div>
                <div className="text-sm font-bold text-[#0B1F4A]">🕐 Dagprofiel — {MN[dpMo]}</div>
                <div className="text-[11px] text-[#8899AA]">Gemiddeld uurprofiel (MW)</div>
              </div>
              <div className="flex gap-1 flex-wrap">
                {MN.map((m, i) => (
                  <button key={i} onClick={() => setDpMo(i)} className={cn(
                    "px-2 py-1 text-[9px] rounded cursor-pointer border",
                    i === dpMo ? "font-bold bg-[#00A3C4] text-white border-[#00A3C4]" : "bg-transparent text-[#8899AA] border-[#D9E2EC] hover:bg-[#F5F8FB]"
                  )}>{m}</button>
                ))}
              </div>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={dpData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.bdr} />
                <XAxis dataKey="h" tick={{ fontSize: 9, fill: C.t3 }} />
                <YAxis tick={{ fontSize: 10, fill: C.t3 }} label={{ value: 'MW', angle: -90, position: 'insideLeft', style: { fontSize: 10, fill: C.t3 } }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Area type="monotone" dataKey="pv" name="PV" fill={C.ch[0]} fillOpacity={.3} stroke={C.ch[0]} strokeWidth={2} />
                <Area type="monotone" dataKey="dem" name="Vraag" fill={C.ch[5]} fillOpacity={.15} stroke={C.ch[5]} strokeWidth={2} />
                <Area type="monotone" dataKey="batDischarge" name="Ontlading" fill="#10B981" fillOpacity={.25} stroke="#10B981" strokeWidth={2} />
                <Area type="monotone" dataKey="grid" name="Grid" fill={C.ch[4]} fillOpacity={.15} stroke={C.ch[4]} strokeWidth={1.5} />
              </AreaChart>
            </ResponsiveContainer>
          </TabsContent>

          {/* ENERGIEBALANS */}
          <TabsContent value="balans" className="mt-0">
            <div className="text-sm font-bold text-[#0B1F4A] mb-3">⚖️ Energiebalans</div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-xs font-semibold text-[#0B1F4A] mb-2">Aanbod</div>
                <div className="space-y-1 text-[11px]">
                  <div className="flex justify-between"><span>☀️ PV Productie</span><strong>{r.pv.toFixed(0)} MWh</strong></div>
                  <div className="flex justify-between"><span>⚡ Grid Import</span><strong>{r.gi.toFixed(0)} MWh</strong></div>
                  <div className="flex justify-between"><span>🏭 Backup</span><strong>{r.bu.toFixed(0)} MWh</strong></div>
                  <div className="flex justify-between border-t pt-1 font-bold"><span>Totaal</span><span>{supply.toFixed(0)} MWh</span></div>
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold text-[#0B1F4A] mb-2">Vraag & Verliezen</div>
                <div className="space-y-1 text-[11px]">
                  <div className="flex justify-between"><span>🔌 Laadvraag</span><strong>{r.dem.toFixed(0)} MWh</strong></div>
                  {r.unmet > 0.0001 && (
                    <div className="flex justify-between text-[#991B1B]">
                      <span>❗ Ongedekt (niet geleverd, niet gefactureerd)</span><strong>{r.unmet.toFixed(1)} MWh</strong>
                    </div>
                  )}
                  <div className="flex justify-between"><span>🔋 BESS Verliezen</span><strong>{r.bLoss.toFixed(1)} MWh</strong></div>
                  <div className="flex justify-between"><span>📤 Grid Export</span><strong>{r.ge.toFixed(0)} MWh</strong></div>
                  <div className="flex justify-between"><span>✂️ Curtailment</span><strong>{r.curt.toFixed(0)} MWh</strong></div>
                  <div className="flex justify-between border-t pt-1 font-bold"><span>Totaal</span><span>{demand.toFixed(0)} MWh</span></div>
                </div>
              </div>
            </div>
            {Math.abs(supply - demand) < 1 ? (
              <div className="mt-3 text-[10px] text-green-600 bg-green-50 rounded-lg px-3 py-2 border border-green-200">
                ✅ Energiebalans klopt — verschil: {Math.abs(supply - demand).toFixed(2)} MWh
              </div>
            ) : (
              <div className="mt-3 text-[10px] text-amber-600 bg-amber-50 rounded-lg px-3 py-2 border border-amber-200">
                ⚠️ Afwijking in energiebalans: {Math.abs(supply - demand).toFixed(1)} MWh ({(Math.abs(supply - demand) / supply * 100).toFixed(2)}%)
              </div>
            )}
          </TabsContent>

          {/* BUSINESS CASE */}
          <TabsContent value="bc" className="mt-0">
            <HubV2BusinessCase
              initialFinancialParams={{ financialParams, deg }}
              onFinancialParamsChange={() => {}}
            />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
